import type { layout } from "@app/domain";
import { type CSSProperties, useEffect, useState } from "react";
import type { TrickView } from "../variants/types";
import { Card, cardLabel } from "./Card";
import type { seatSlots } from "./seatOrder";
import { COLLECT_MS, REST_MS, type TrickStage, trickStage } from "./trickStage";

type Slots = ReturnType<typeof seatSlots>;

/**
 * Where each seat's card lies around the table's middle and its tilt (design,
 * in units of a 72 px card), and where it flies in from.
 */
const TRICK_POSE: Record<
  keyof Slots,
  { x: number; y: number; rot: number; dx: number; dy: number }
> = {
  seatTop: { x: -6, y: -40, rot: -8, dx: 0, dy: -90 },
  seatLeft: { x: -52, y: 4, rot: -78, dx: -110, dy: 0 },
  seatRight: { x: 50, y: -2, rot: 84, dx: 110, dy: 0 },
  seatSelf: { x: 4, y: 36, rot: 6, dx: 0, dy: 120 },
};

type Pose = (typeof TRICK_POSE)[keyof Slots];

function poseStyle(pose: Pose, rect: layout.Rect, card: { w: number; h: number }) {
  const k = card.w / 72;
  return {
    left: rect.w / 2 + pose.x * k - card.w / 2,
    top: rect.h / 2 + pose.y * k - card.h / 2,
    "--rot": `${pose.rot}deg`,
    "--dx": `${pose.dx * k}px`,
    "--dy": `${pose.dy * k}px`,
  };
}

/**
 * The trick just won: the four cards gather in the middle, last card on top,
 * then shrink together toward the winner. Runs once per completed trick.
 */
function CollectingTrick({
  trick,
  bySeat,
  rect,
  card,
}: {
  trick: NonNullable<TrickView["lastTrick"]>;
  bySeat: Map<number, keyof Slots>;
  rect: layout.Rect;
  card: { w: number; h: number };
}) {
  const winner = TRICK_POSE[bySeat.get(trick.winner) ?? "seatTop"];
  const k = card.w / 72;
  const centre = { x: (rect.w - card.w) / 2, y: (rect.h - card.h) / 2 };
  return (
    <>
      {trick.plays.map((play, i) => {
        const base = poseStyle(TRICK_POSE[bySeat.get(play.seat) ?? "seatTop"], rect, card);
        const vars = {
          ...base,
          zIndex: 20 + i,
          "--cx": `${centre.x - base.left}px`,
          "--cy": `${centre.y - base.top}px`,
          "--tx": `${winner.dx * k * 2.2}px`,
          "--ty": `${winner.dy * k * 2.2}px`,
        } as CSSProperties;
        return (
          <div key={cardLabel(play.card)} className="absolute animate-collect" style={vars}>
            <Card card={play.card} width={card.w} height={card.h} state="plain" />
          </div>
        );
      })}
    </>
  );
}

/** The last completed trick's stage (see `trickStage`), redrawn when it moves on. */
function useCollecting(view: TrickView): TrickStage {
  const last = view.lastTrick;
  const id = last === null ? "" : last.plays.map((p) => cardLabel(p.card)).join(",");
  const [seen, setSeen] = useState({ id: "", at: 0, resting: false });
  if (id !== "" && seen.id !== id)
    setSeen({ id, at: Date.now(), resting: view.trick.winner !== null });
  const [, redraw] = useState(0);
  // Keyed on the trick alone: every snapshot brings a new object, and re-running on it
  // used to cancel the timer that ends the collect.
  useEffect(() => {
    if (id === "") return;
    const timers = [REST_MS, REST_MS + COLLECT_MS].map((ms) =>
      setTimeout(() => redraw((n) => n + 1), ms),
    );
    return () => {
      for (const timer of timers) clearTimeout(timer);
    };
  }, [id]);
  if (id === "" || seen.id !== id) return "rest";
  return trickStage(Date.now() - seen.at, seen.resting);
}

/** The cards on the table, each by the seat that played it; a won trick is collected on top. */
export function Trick({
  view,
  slots,
  rect,
  card,
}: {
  view: TrickView;
  slots: Slots;
  rect: layout.Rect;
  card: { w: number; h: number };
}) {
  const bySeat = new Map<number, keyof Slots>();
  for (const key of Object.keys(slots) as (keyof Slots)[]) bySeat.set(slots[key], key);
  const stage = useCollecting(view);
  const last = view.lastTrick;
  // Resting, the won trick lies as played; collecting, the overlay draws it; then never again.
  const taken = new Set(
    stage === "rest" || last === null ? [] : last.plays.map((p) => cardLabel(p.card)),
  );
  const plays = view.trick.plays.filter((p) => !taken.has(cardLabel(p.card)));
  return (
    <div className="relative h-full w-full">
      {plays.map((play, i) => (
        <div
          key={cardLabel(play.card)}
          className="absolute animate-deal"
          style={
            {
              ...poseStyle(TRICK_POSE[bySeat.get(play.seat) ?? "seatTop"], rect, card),
              zIndex: i,
            } as CSSProperties
          }
        >
          <Card card={play.card} width={card.w} height={card.h} state="plain" />
        </div>
      ))}
      {stage === "collecting" && last !== null ? (
        <CollectingTrick trick={last} bySeat={bySeat} rect={rect} card={card} />
      ) : null}
    </div>
  );
}
